import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import { LeadStatus } from 'types/enums/LeadStatus'
import { OfferStatus } from 'types/enums/OfferStatus'
import { ReservationStatus } from 'types/enums/ReservationStatus'
import { CompanyActivationStatus } from 'types/enums/CompanyActivationStatus'
import { CrmLifecycleStatus } from 'types/enums/CrmLifecycleStatus'

const REJECTION_LEAD_STATUSES = new Set<string>([
    LeadStatus.Rejection,
    LeadStatus.Disqualified,
])

const WON_LEAD_STATUSES = new Set<string>([
    LeadStatus.Acceptance,
    LeadStatus.Finished,
])

const PIPELINE_LEAD_STATUSES = new Set<string>([
    LeadStatus.OpportunityCreated,
    LeadStatus.Quotation,
    LeadStatus.Acceptance,
])

const PIPELINE_OFFER_STATUSES = new Set<string>([
    OfferStatus.New,
    OfferStatus.Sent,
    OfferStatus.Accepted,
    OfferStatus.Reconfirm,
])

const FINISHED_RESERVATION_STATUSES = new Set<string>([
    ReservationStatus.Finished,
    ReservationStatus.Checked,
])

const PIPELINE_RESERVATION_STATUSES = new Set<string>([
    ReservationStatus.Creating,
    ReservationStatus.Pending,
    ReservationStatus.Confirmed,
    ReservationStatus.Reserved,
    ReservationStatus.Rebooking,
    ReservationStatus.GroupPackage,
])

const isFutureDate = (date?: string | null): boolean => {
    if (!date) return false
    const parsedDate = new Date(date)
    if (Number.isNaN(parsedDate.getTime())) return false
    return parsedDate.getTime() >= Date.now()
}

/** Canonical enum values for fast O(1) lookup. */
const LIFECYCLE_STATUS_VALUES = new Set<string>(Object.values(CrmLifecycleStatus))

/**
 * Legacy / alternate spellings (German DE backend, old API values) that map
 * to canonical CrmLifecycleStatus values.
 */
const LIFECYCLE_STATUS_ALIASES: Partial<Record<string, CrmLifecycleStatus>> = {
    bestandskunde: CrmLifecycleStatus.ExistingCustomer,
    existing: CrmLifecycleStatus.ExistingCustomer,
    absage: CrmLifecycleStatus.Rejection,
    archiv: CrmLifecycleStatus.Archive,
    archived: CrmLifecycleStatus.Archive,
}

const normalizeLifecycleStatus = (
    status?: string | null
): CrmLifecycleStatus | null => {
    if (!status) return null
    const key = String(status).trim().toLowerCase()
    if (LIFECYCLE_STATUS_ALIASES[key]) return LIFECYCLE_STATUS_ALIASES[key]!
    if (LIFECYCLE_STATUS_VALUES.has(key)) return key as CrmLifecycleStatus
    return null
}

export const resolveCompanyLifecycleStatus = (
    company: Partial<Company> & Record<string, any>
): CrmLifecycleStatus => {
    const explicit = normalizeLifecycleStatus(
        company.account_status ?? company.lifecycle_status
    )
    if (explicit) return explicit

    const activationStatus = String(company.status || '').toLowerCase()
    if (
        activationStatus === CompanyActivationStatus.Archived ||
        activationStatus === CompanyActivationStatus.Disabled
    ) {
        return CrmLifecycleStatus.Archive
    }

    const reservations = Array.isArray(company.reservations)
        ? company.reservations
        : []
    const offers = Array.isArray(company.offers) ? company.offers : []
    const leads = Array.isArray(company.leads) ? company.leads : []

    const hasCompletedEvent = reservations.some((reservation: any) =>
        FINISHED_RESERVATION_STATUSES.has(String(reservation?.status || ''))
    )
    if (hasCompletedEvent) {
        return CrmLifecycleStatus.ExistingCustomer
    }

    const hasPipelineReservation = reservations.some((reservation: any) => {
        const status = String(reservation?.status || '')
        return (
            PIPELINE_RESERVATION_STATUSES.has(status) ||
            (status !== ReservationStatus.Finished && isFutureDate(reservation?.date))
        )
    })

    const hasPipelineOffer = offers.some((offer: any) =>
        PIPELINE_OFFER_STATUSES.has(String(offer?.status || ''))
    )

    const hasPipelineOpportunity = leads.some((lead: any) =>
        PIPELINE_LEAD_STATUSES.has(String(lead?.status || ''))
    )

	if(hasPipelineReservation) {
		return CrmLifecycleStatus.ExistingCustomer;
	}

    if (hasPipelineReservation || hasPipelineOffer || hasPipelineOpportunity) {
        return CrmLifecycleStatus.Pipeline
    }

    const leadStatuses = leads
        .map((lead: any) => String(lead?.status || ''))
        .filter(Boolean)

    const hasAnyLeadStatus = leadStatuses.length > 0
    const hasWonLead = leadStatuses.some((status) => WON_LEAD_STATUSES.has(status))
    const hasRejectedLead = leadStatuses.some((status) =>
        REJECTION_LEAD_STATUSES.has(status)
    )

    if (hasAnyLeadStatus && hasRejectedLead && !hasWonLead) {
        return CrmLifecycleStatus.Rejection
    }

    return CrmLifecycleStatus.Database
}

export const resolveContactLifecycleStatus = (
    contact: Partial<CompanyContact> & Record<string, any>
): CrmLifecycleStatus => {
    const explicit = normalizeLifecycleStatus(
        contact.contact_status ?? contact.account_status ?? contact.lifecycle_status
    )
    if (explicit) return explicit

    const companies = Array.isArray(contact.companies) ? contact.companies : []
    if (companies.length > 0) {
        const companyStatuses = companies.map((company: any) =>
            resolveCompanyLifecycleStatus(company)
        )
        if (companyStatuses.includes(CrmLifecycleStatus.Archive)) {
            return CrmLifecycleStatus.Archive
        }
        if (companyStatuses.includes(CrmLifecycleStatus.ExistingCustomer)) {
            return CrmLifecycleStatus.ExistingCustomer
        }
        if (companyStatuses.includes(CrmLifecycleStatus.Pipeline)) {
            return CrmLifecycleStatus.Pipeline
        }
        if (companyStatuses.includes(CrmLifecycleStatus.Rejection)) {
            return CrmLifecycleStatus.Rejection
        }
    }

    const pseudoCompany: Partial<Company> & Record<string, any> = {
        status: CompanyActivationStatus.Active,
        leads: contact.leads,
        offers: contact.offers,
        reservations: [],
    }

    return resolveCompanyLifecycleStatus(pseudoCompany)
}

export const useCrmLifecycleStatusOptions = () => {
    const { t } = useTranslation()

    return useMemo(
        () => [
            { value: CrmLifecycleStatus.Database, label: t('Database') },
            { value: CrmLifecycleStatus.Pipeline, label: t('Pipeline') },
            {
                value: CrmLifecycleStatus.ExistingCustomer,
                label: t('Existing customer'),
            },
            { value: CrmLifecycleStatus.Rejection, label: t('Rejection') },
            { value: CrmLifecycleStatus.Archive, label: t('Archived') },
        ],
        [t]
    )
}
